import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import BrandMark from "@/components/BrandMark";
import { useLocale } from "@/lib/locale";
import { track } from "@/lib/track";

export default function Landing() {
    const { t } = useLocale();
    const heroRef = useRef(null);
    const [heroProgress, setHeroProgress] = useState(0);

    // Scroll-driven hero fade. As the user scrolls down, the hero progressively
    // fades & lifts (progress 0 → 1 over the first viewport-height of scroll).
    useEffect(() => {
        let raf = null;
        const onScroll = () => {
            if (raf) return;
            raf = requestAnimationFrame(() => {
                const el = heroRef.current;
                if (!el) { raf = null; return; }
                const h = el.offsetHeight || window.innerHeight;
                const p = Math.min(1, Math.max(0, window.scrollY / (h * 0.7)));
                setHeroProgress(p);
                raf = null;
            });
        };
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => {
            window.removeEventListener("scroll", onScroll);
            if (raf) cancelAnimationFrame(raf);
        };
    }, []);

    return (
        <div className="grain min-h-screen candle-halo">
            <Header />

            {/* Premium HERO — centered, ceremonial logo, whispered motion.
                Background: layered gold haloes + vignette + soft grain + slow particles.
                Hero fades/lifts as user scrolls (JS-driven --hero-progress CSS var). */}
            <section
                ref={heroRef}
                className="relative overflow-hidden min-h-[86vh] md:min-h-[88vh] flex items-center justify-center py-20 md:py-24"
                data-testid="hero-section"
                style={{ "--hero-progress": heroProgress }}
            >
                {/* Ambient background — deep warm base + warm gold focal glow centered */}
                <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
                    <div className="absolute inset-0 bg-ink-deep" />
                    {/* Central warm halo — the "candle" behind the logo */}
                    <div
                        className="absolute inset-0"
                        style={{
                            background:
                                "radial-gradient(820px 620px at 50% 44%, rgba(232, 192, 125, 0.34), rgba(72, 58, 46, 0.22) 48%, transparent 80%)",
                        }}
                    />
                    {/* Soft counter-glow upper-right for depth asymmetry */}
                    <div
                        className="absolute inset-0"
                        style={{
                            background:
                                "radial-gradient(680px 480px at 82% 22%, rgba(212, 176, 118, 0.12), transparent 72%)",
                        }}
                    />
                    {/* Vertical light rays — barely-there warm streaks */}
                    <div
                        className="absolute inset-0 mix-blend-soft-light opacity-50"
                        style={{
                            background:
                                "repeating-linear-gradient(100deg, transparent 0px, transparent 160px, rgba(246, 211, 152, 0.07) 161px, transparent 260px)",
                        }}
                    />
                    {/* Softer central vignette — keeps focus on the logo without dimming the page. */}
                    <div
                        className="absolute inset-0"
                        style={{
                            background:
                                "radial-gradient(ellipse at 50% 50%, transparent 36%, rgba(15, 12, 9, 0.36) 76%, rgba(10, 8, 6, 0.74) 100%)",
                        }}
                    />
                    {/* Slow gold particles — barely visible, floating upward */}
                    <div className="absolute inset-0" data-testid="hero-particles">
                        {[
                            { l: "12%", t: "78%", d: "0s",  s: "1.8px" },
                            { l: "22%", t: "85%", d: "-6s", s: "1.5px" },
                            { l: "34%", t: "72%", d: "-12s", s: "2px"  },
                            { l: "48%", t: "88%", d: "-3s", s: "1.5px" },
                            { l: "58%", t: "76%", d: "-15s", s: "2.2px"},
                            { l: "68%", t: "82%", d: "-9s",  s: "1.8px"},
                            { l: "78%", t: "74%", d: "-18s", s: "1.5px"},
                            { l: "86%", t: "86%", d: "-5s",  s: "1.8px"},
                            { l: "17%", t: "65%", d: "-21s", s: "1.5px"},
                            { l: "82%", t: "62%", d: "-11s", s: "1.8px"},
                        ].map((p, i) => (
                            <span
                                key={i}
                                className="hero-particle"
                                style={{
                                    left: p.l,
                                    top: p.t,
                                    width: p.s,
                                    height: p.s,
                                    animationDelay: p.d,
                                }}
                            />
                        ))}
                    </div>
                    {/* Bottom fade into page body */}
                    <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-b from-transparent to-ink-deep" />
                    {/* Film grain */}
                    <div className="absolute inset-0 grain opacity-50" />
                </div>

                {/* Centered content column — logo, title, subtitle, CTAs */}
                <div className="relative w-full max-w-3xl mx-auto px-6 md:px-10 text-center hero-scroll-fade">
                    {/* Logo — ceremonial, breathing, glowing */}
                    <div className="flex justify-center mb-10 md:mb-12">
                        <div
                            className="hero-mark-wrap hero-mark-shimmer relative inline-block"
                            data-testid="hero-logo"
                            style={{ animationDelay: "0.2s" }}
                        >
                            <BrandMark size={110} ceremonial breathing />
                        </div>
                    </div>

                    {/* Title */}
                    <h1
                        className="h-serif text-5xl sm:text-6xl lg:text-[4.5rem] leading-[1.08] tracking-tight hero-fade-up"
                        style={{ animationDelay: "0.45s" }}
                        data-testid="hero-title"
                    >
                        {t("hero.title_a")}
                        <br />
                        <span className="italic text-ink-goldHi">{t("hero.title_b")}</span>
                    </h1>

                    {/* Subtitle */}
                    <p
                        className="mt-8 md:mt-10 max-w-xl mx-auto text-[1.1rem] md:text-[1.18rem] leading-[1.65] text-ink-textHigh hero-fade-up"
                        style={{ animationDelay: "0.85s" }}
                        data-testid="hero-subtitle"
                    >
                        {t("hero.body")}
                    </p>

                    {/* CTAs */}
                    <div
                        className="mt-10 md:mt-12 flex items-center justify-center gap-4 md:gap-5 hero-cta-enter"
                        style={{ animationDelay: "1.25s" }}
                    >
                        <Link
                            to="/signup"
                            className="btn-gold"
                            data-testid="hero-cta-primary"
                        >
                            {t("hero.cta_primary")}
                        </Link>
                        <Link
                            to="/login"
                            className="btn-ghost-gold"
                            data-testid="hero-cta-secondary"
                        >
                            {t("hero.cta_signin")}
                        </Link>
                    </div>

                    {/* Third tertiary option — deliberately thinner than the
                        two primary CTAs above. Reads as "there's also this,
                        if you're not ready to subscribe yet". Still visually
                        subordinate to the primary funnel (no border/button,
                        small text) but now above the fold on most viewports. */}
                    <div
                        className="mt-5 md:mt-6 flex items-center justify-center hero-fade-up"
                        style={{ animationDelay: "1.45s" }}
                    >
                        <Link
                            to="/free-card"
                            onClick={() => track("free_card_started", { from: "hero" })}
                            className="text-sm text-ink-textMid font-serif italic hover:text-ink-gold transition-colors"
                            data-testid="hero-free-card-link"
                        >
                            oppure{" "}
                            <span className="not-italic tracking-wide text-ink-gold border-b border-ink-gold/40 hover:border-ink-gold pb-0.5 transition-colors">
                                crea un ricordo gratis →
                            </span>
                        </Link>
                    </div>

                    {/* Quiet AI disclaimer — well below the CTAs. */}
                    <p
                        className="mt-12 md:mt-14 max-w-md mx-auto text-[0.72rem] leading-relaxed text-ink-textLow/70 italic font-serif hero-fade-up"
                        style={{ animationDelay: "1.6s" }}
                        data-testid="hero-ai-disclaimer"
                    >
                        {t("hero.disclaimer")}
                    </p>
                </div>
            </section>

            {/* How it works */}
            <section className="relative max-w-6xl mx-auto px-6 md:px-12 py-28 md:py-36">
                <p className="label-eyebrow mb-8">{t("landing.steps_eyebrow")}</p>
                <h2 className="h-serif text-4xl sm:text-5xl lg:text-6xl max-w-2xl mb-20 leading-[1.1]">
                    {t("landing.steps_title")}
                </h2>
                <div className="grid md:grid-cols-4 gap-12 md:gap-16">
                    {[
                        { n: "01", t: t("landing.step1_t"), d: t("landing.step1_d") },
                        { n: "02", t: t("landing.step2_t"), d: t("landing.step2_d") },
                        { n: "03", t: t("landing.step3_t"), d: t("landing.step3_d") },
                        { n: "04", t: t("landing.step4_t"), d: t("landing.step4_d") },
                    ].map((s) => (
                        <div key={s.n} className="border-t border-ink-border pt-7">
                            <div className="label-eyebrow text-ink-gold">{s.n}</div>
                            <h3 className="font-serif text-[1.7rem] mt-4 text-ink-textHigh leading-tight">{s.t}</h3>
                            <p className="mt-4 text-ink-textMid text-lg leading-[1.6]">{s.d}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Memory scene — dual composition as per reference.
                Left: sunbeam in a dark room (light gently breathes).
                Right: soft curtain revealing a distant landscape (curtain gently sways).
                Layout: frames on top, reflective text underneath. */}
            <section className="relative max-w-6xl mx-auto px-6 md:px-12 pb-32" data-testid="memory-scene">
                <p className="label-eyebrow mb-8">{t("landing.memory_eyebrow")}</p>

                {/* Dual frames */}
                <div className="grid grid-cols-2 gap-4 md:gap-6 mb-16 md:mb-20" data-testid="evocative-frames">
                    {/* LEFT — sunbeam with dust */}
                    <div
                        className="photo-frame aspect-[3/4] relative overflow-hidden bg-ink-deep"
                        data-testid="evocative-silhouette"
                    >
                        <img
                            src="/hero/memory-left.jpg"
                            alt=""
                            aria-hidden="true"
                            loading="lazy"
                            className="absolute inset-0 w-full h-full object-cover ray-breathe"
                        />
                        {/* Animated dust motes — drifting through the beam */}
                        <div className="dust-field absolute inset-0" aria-hidden="true" data-testid="dust-field">
                            {/* Staggered motes positioned roughly along the sunbeam diagonal */}
                            <span className="dust-mote a" style={{ left: "58%", animationDelay: "0s"    }} />
                            <span className="dust-mote b" style={{ left: "62%", animationDelay: "-4s"   }} />
                            <span className="dust-mote c" style={{ left: "55%", animationDelay: "-9s"   }} />
                            <span className="dust-mote a" style={{ left: "66%", animationDelay: "-14s"  }} />
                            <span className="dust-mote b" style={{ left: "50%", animationDelay: "-18s"  }} />
                            <span className="dust-mote c" style={{ left: "70%", animationDelay: "-6s"   }} />
                            <span className="dust-mote a" style={{ left: "45%", animationDelay: "-11s", width: "3px", height: "3px" }} />
                            <span className="dust-mote b" style={{ left: "74%", animationDelay: "-2s",  width: "1.5px", height: "1.5px" }} />
                            <span className="dust-mote c" style={{ left: "40%", animationDelay: "-20s" }} />
                            <span className="dust-mote a" style={{ left: "78%", animationDelay: "-16s", width: "1.5px", height: "1.5px" }} />
                        </div>
                        {/* Edge vignette to blend into page */}
                        <div
                            className="absolute inset-0 pointer-events-none"
                            style={{
                                background:
                                    "radial-gradient(ellipse at 50% 50%, transparent 55%, rgba(10,8,6,0.5) 100%)",
                            }}
                        />
                        <div className="absolute inset-0 grain opacity-30 pointer-events-none" />
                    </div>
                    {/* RIGHT — curtain revealing a distant landscape */}
                    <div
                        className="photo-frame aspect-[3/4] relative overflow-hidden bg-ink-surface"
                        data-testid="evocative-curtains"
                    >
                        <img
                            src="/hero/memory-right.jpg"
                            alt=""
                            aria-hidden="true"
                            loading="lazy"
                            className="absolute inset-0 w-full h-full object-cover curtain-sway"
                        />
                        {/* Wind gust overlay — a faint luminous sweep crossing the curtain occasionally */}
                        <div className="wind-gust" aria-hidden="true" data-testid="wind-gust" />
                        {/* Soft warm-to-dark vignette to keep edges quiet */}
                        <div
                            className="absolute inset-0 pointer-events-none"
                            style={{
                                background:
                                    "radial-gradient(ellipse at 55% 50%, transparent 55%, rgba(10,8,6,0.45) 100%)",
                            }}
                        />
                        <div className="absolute inset-0 grain opacity-30 pointer-events-none" />
                    </div>
                </div>

                {/* Reflective text */}
                <div className="max-w-3xl space-y-8">
                    <h2 className="h-serif text-4xl sm:text-5xl leading-[1.1]">
                        {t("landing.memory_title_a")} <span className="italic">{t("landing.memory_title_i")}</span> {t("landing.memory_title_b")}
                    </h2>
                    <p className="text-ink-textHigh/90 text-xl leading-[1.65] whitespace-pre-line">
                        {t("landing.memory_body")}
                    </p>
                    <p className="text-ink-textHigh leading-[1.55] italic font-serif text-2xl whitespace-pre-line">
                        {t("landing.memory_quote")}
                    </p>
                </div>
            </section>

            {/* Trust & privacy — three quiet statements. No icons, no noise. */}
            <section
                className="relative max-w-6xl mx-auto px-6 md:px-12 pb-28 md:pb-36"
                data-testid="trust-section"
            >
                <div className="border-t border-ink-border pt-16 md:pt-20">
                    <p className="label-eyebrow mb-10 md:mb-14">{t("trust.eyebrow")}</p>
                    <div className="grid md:grid-cols-3 gap-10 md:gap-16">
                        <p className="font-serif text-[1.4rem] md:text-[1.5rem] text-ink-textHigh leading-[1.45]" data-testid="trust-privacy">
                            <span className="text-ink-gold">—</span>&nbsp;&nbsp;{t("trust.privacy")}
                        </p>
                        <p className="font-serif text-[1.4rem] md:text-[1.5rem] text-ink-textHigh leading-[1.45]" data-testid="trust-delete">
                            <span className="text-ink-gold">—</span>&nbsp;&nbsp;{t("trust.delete")}
                        </p>
                        <p className="font-serif text-[1.4rem] md:text-[1.5rem] text-ink-textHigh leading-[1.45]" data-testid="trust-no-share">
                            <span className="text-ink-gold">—</span>&nbsp;&nbsp;{t("trust.no_share")}
                        </p>
                    </div>
                </div>
            </section>

            <footer className="border-t border-ink-border">
                <div className="max-w-6xl mx-auto px-6 md:px-12 py-10 flex flex-col gap-6">
                    <div className="flex flex-wrap gap-6 items-center justify-between">
                        <div className="flex items-center gap-3">
                            <BrandMark size={28} breathing={false} />
                            <p className="text-sm text-ink-textMid tracking-wide">
                                {t("footer.tag")}
                            </p>
                        </div>
                        <div className="flex flex-wrap gap-5 text-sm text-ink-textMid">
                            <Link to="/signup" className="hover:text-ink-textHigh" data-testid="footer-begin">{t("nav.begin")}</Link>
                            <Link to="/login" className="hover:text-ink-textHigh" data-testid="footer-signin">{t("nav.signin")}</Link>
                            <Link to="/legal" className="hover:text-ink-textHigh underline underline-offset-4 decoration-ink-gold/40" data-testid="footer-legal">{t("footer.legal")}</Link>
                            <Link to="/legal/report" className="hover:text-ink-textHigh" data-testid="footer-report">{t("footer.report")}</Link>
                        </div>
                    </div>
                    <div className="border-t border-ink-borderSubtle pt-5 space-y-2 text-center" data-testid="footer-legal-lines">
                        <p className="font-serif italic text-ink-textMid text-sm md:text-base leading-relaxed" data-testid="footer-legal-line1">
                            {t("footer.line1")}
                        </p>
                        <p className="text-xs text-ink-textLow tracking-wide leading-relaxed" data-testid="footer-legal-line2">
                            {t("footer.line2")}
                        </p>
                        <p className="pt-1 text-xs text-ink-textLow">
                            <a
                                href="mailto:os.otherside.app@gmail.com"
                                className="text-ink-gold hover:text-ink-goldHi underline underline-offset-4 decoration-ink-gold/40 hover:decoration-ink-gold transition-colors"
                                data-testid="footer-legal-mailto"
                            >
                                os.otherside.app@gmail.com
                            </a>
                        </p>
                    </div>
                    <p className="text-xs text-ink-textLow tracking-wide" data-testid="footer-cookie">
                        {t("cookie.text")}
                    </p>
                </div>
            </footer>
        </div>
    );
}
